import type { PropertyValueMap } from 'lit';
import { css, html, LitElement, nothing } from 'lit';

import { customElement, property, state } from 'lit/decorators.js';
import { displayAlert } from 'src/utilities/display-alert.js';
import { openCriiptoAuth, openHelseIdAuth } from 'src/store/oauth-browser';
import { runningUnderNativePlatform } from 'src/store/running-under-native-platform';
import { isEmailValid } from '../../store/selectors/utilities/isEmailValid';
import { t } from 'src/i18n';
import { recordEvent } from 'src/utilities/monitoring.js';

type LoginInputField = 'username' | 'password';

interface LoginInputs {
  username?: HTMLInputElement;
  password?: HTMLInputElement;
}

/**
 * Viser innloggingskjemaet, inkludert alternativer for å registrere ny konto eller få
 * tilsendt nytt passord
 *
 * @fires request-login submit login request
 * @fires request-password submit password request
 *
 */
@customElement('d-login')
export class DLogin extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
      min-height: 100vh;
      background: var(--outskirts-background-color);
      padding-top: 60px;
      box-sizing: border-box;
    }

    :host([in-signup]) {
      min-height: auto;
      margin-top: 6px;
      margin-bottom: 12px;
      border-radius: 6px;
      padding-top: 20px;
      padding-bottom: 20px;
    }

    #loginForm {
      max-width: 300px;
      margin: 0 auto;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    #loginForm > * {
      margin-bottom: 10px;
    }

    #loginForm .logo {
      width: 146px;
      height: 37px;
      background: url(/images/trinnvis-logo-white.svg) 50% 50% no-repeat;
      background-size: contain;
      margin-bottom: 14px;
    }

    #loginForm input[type='email'],
    #loginForm input[type='password'] {
      font-family: var(--mainSans);
      font-size: 15px;
      text-align: center;
      border-radius: 4px;
    }

    #loginForm input[type='submit'] {
      margin-top: 10px;
      width: auto;
      padding: 8px 12px;
      display: inline-block;
      font-size: 11px;
      letter-spacing: 2px;
      font-weight: 500;
      color: white;
      background-color: hsla(0, 0%, 0%, 0.2);
      border-radius: 4px;
    }

    #loginForm input[type='button'] {
      margin-top: 10px;
      width: auto;
      padding: 8px 12px;
      display: inline-block;
      font-size: 11px;
      letter-spacing: 2px;
      font-weight: 500;
      color: white;
      background-color: hsla(0, 0%, 0%, 0.2);
      border-radius: 4px;
    }

    body:not(.touch) #loginForm input[type='submit']:hover {
      background-color: hsla(0, 0%, 0%, 0.3);
    }

    body:not(.touch) #loginForm input[type='button']:hover {
      background-color: hsla(0, 0%, 0%, 0.3);
    }

    body:not(.touch) #loginForm input[type='submit']:hover {
      background-color: hsla(0, 0%, 0%, 0.3);
    }

    #loginForm .alternatives {
      width: 100%;
      text-align: center;
      margin-top: 20px;
      font-weight: 200;
      color: white;
      cursor: pointer;
    }

    #loginForm .alternatives > * {
      margin-bottom: 10px;
      opacity: 0.8;
    }

    body:not(.touch) #loginForm .alternatives > *:hover {
      opacity: 1;
    }

    input {
      border: none;
      box-shadow: none;
      padding: 5px 10px 6px 10px;
      resize: none;
      border-radius: 0;
      appearance: none;
      writing-mode: horizontal-tb;
      width: 100%;
      box-sizing: border-box;
      font-family: var(--mainSans), sans-serif;
      font-weight: 200;
      font-size: 15px;
      line-height: 160%;
      color: black;
    }

    input[type='submit'] {
      cursor: pointer;
      text-transform: uppercase;
    }

    .secureLogin {
      margin-top: 10px;
      border: 1px solid hsla(0, 0%, 100%, 0.4);
      border-radius: 12px;
      padding: 106px 30px 30px 30px;
      background: transparent url(/images/personal-data-items-gray.svg) 50% 20px no-repeat;
      background-size: 70px 70px;
      text-align: center;
    }

    #loginForm .secureLogin h2 {
      font-size: 20px;
      line-height: 120%;
      color: white;
    }

    #loginForm p {
      font-size: 15px;
      line-height: 150%;
      text-align: center;
    }

    #loginForm .secureLogin input {
      margin: 10px;
    }
  `;
  /**
   * Display login failed message.
   */
  @property({ type: Boolean })
  loginFailed = false;
  /**
   * The mode. Used when linking to a secure authentication method. If this value is `link` then use
   */
  @property({ type: String })
  mode = '';
  /**
   * The location search. Set this to value of `window.location.search` when mode is `link`.
   */
  @property({ type: String })
  locationSearch = '';
  @state()
  private username = '';
  @state()
  private password = '';
  @state()
  private loginErrorInvalidEmail = false;
  @state()
  private loginErrorMissingPassword = false;

  private autoLoginChecked = false;
  private instrumentationAbortController?: AbortController;
  private reportedMountAnomaly = false;
  private reportedPointerBlocked = false;
  private reportedKeyWithoutInput = false;

  get _usernameInput(): Element {
    const slotted = this.shadowRoot?.querySelector('slot#loginName');
    let element = (slotted as HTMLSlotElement)?.assignedElements()[0];

    // To execute the while loop a maximum of 10 times
    // If an input element is found within the first 10 iterations, the function will return that element. Otherwise, it will return undefined.
    let loopCount = 0;
    while (element?.tagName !== 'INPUT' && loopCount < 10) {
      element = (element as HTMLSlotElement)?.assignedElements()[0];
      loopCount++;
    }

    return element;
  }

  get _passwordInput(): Element {
    const slotted = this.shadowRoot?.querySelector('slot#password');
    let element = (slotted as HTMLSlotElement)?.assignedElements()[0];

    // To execute the while loop a maximum of 10 times
    // If an input element is found within the first 10 iterations, the function will return that element. Otherwise, it will return undefined.
    let loopCount = 0;
    while (element?.tagName !== 'INPUT' && loopCount < 10) {
      element = (element as HTMLSlotElement)?.assignedElements()[0];
      loopCount++;
    }

    return element;
  }

  private get authDescription() {
    const urlParams = new URLSearchParams(this.locationSearch);
    let encoded = urlParams.get('u');
    if (encoded !== null) {
      encoded = encoded.replace('-', '+').replace('_', '/');
      while (encoded.length % 4) encoded += '=';

      const userInformation = JSON.parse(atob(encoded));

      console.log(userInformation);

      return userInformation.name;
    }

    return '';
  }

  private get provider() {
    const urlParams = new URLSearchParams(this.locationSearch);
    const encoded = urlParams.get('p');
    if (encoded !== null) {
      return encoded;
    }

    return '';
  }

  private get providerDescription() {
    const providerDescriptions = {
      oidc: 'HelseID',
      criipto: 'BankID',
    };

    return providerDescriptions[this.provider] ?? '';
  }

  private get isLinkMode() {
    return this.mode === 'link';
  }

  private login(event: SubmitEvent) {
    event.preventDefault();
    console.log('Login', this.username);
    this.loginErrorInvalidEmail = false;
    this.loginErrorMissingPassword = false;
    this.loginFailed = false;
    if (!isEmailValid(this.username)) {
      this.loginErrorInvalidEmail = true;
      return;
    }
    if (!this.password.length) {
      this.loginErrorMissingPassword = true;
      return;
    }

    if (this.mode === 'link') {
      const user = {
        username: this.username,
        password: this.password,
        provider: this.provider,
      };

      this.dispatchEvent(new CustomEvent('request-link', { bubbles: true, composed: true, detail: user }));
    } else {
      const user = {
        username: this.username,
        password: this.password,
      };
      this.dispatchEvent(
        new CustomEvent('request-login', {
          bubbles: true,
          composed: true,
          detail: { user, loginFailed: () => (this.loginFailed = true) },
        }),
      );
    }
  }

  private sendPassword() {
    if (!isEmailValid(this.username)) {
      displayAlert(t('login.invalidEmailForPassword'));
      return;
    }
    this.dispatchEvent(
      new CustomEvent('request-password', { bubbles: true, composed: true, detail: { email: this.username } }),
    );

    displayAlert(t('login.passwordSent', { email: this.username }));
  }

  private async loginAuth() {
    try {
      await openHelseIdAuth(true);
    } catch (error) {
      // openHelseIdAuth already shows a user alert and re-throws; catch here
      // so it does not become an unhandled rejection in monitoring. (issue #1531)
      console.warn('[login] HelseID auth failed', error);
    }
  }

  private async loginAuthCriipto() {
    try {
      await openCriiptoAuth(true);
    } catch (error) {
      console.warn('[login] BankID (Criipto) auth failed', error);
    }
  }

  private checkAutoLogin() {
    // Prevent multiple auto-login attempts
    if (this.autoLoginChecked) {
      return;
    }

    // Use locationSearch if available, otherwise check window.location directly
    const searchString = this.locationSearch || window.location.search;
    const query = searchString.slice(1);
    const params = new URLSearchParams(query);

    // Check for auto-login parameters
    const auto = params.get('auto');
    const provider = params.get('provider');

    // If auto=1 and provider=helseid, automatically trigger HelseID login
    // Auto-login from start.trinnvis.no does not force prompt to allow SSO reuse
    if (auto === '1' && provider === 'helseid') {
      console.log('Auto-login detected for HelseID, triggering authentication...');
      this.autoLoginChecked = true;
      // Fire-and-forget: attach a catch so a rejected auth (network/plugin
      // failure) does not become an unhandled rejection. The auth function
      // already shows a user alert on failure. (issue #1531)
      openHelseIdAuth(false).catch((error) => {
        console.warn('[login] HelseID auto-login failed', error);
      });
    } else if (auto === '1' && provider === 'bankid') {
      console.log('Auto-login detected for BankID, triggering authentication...');
      this.autoLoginChecked = true;
      openCriiptoAuth(false).catch((error) => {
        console.warn('[login] BankID (Criipto) auto-login failed', error);
      });
    }
  }

  private signup() {
    window.location.href = 'https://trinnvis.no/signup';
  }

  private gotoSite() {
    window.location.href = 'https://trinnvis.no';
  }

  private onLoginNameChange(e) {
    this.username = e.target.value;
  }

  private onPasswordChange(e) {
    this.password = e.target.value;
  }

  private getInputStyle() {
    return 'border: none;box-shadow: none;padding: 5px 10px 6px 10px;resize: none;border-radius: 0;appearance: none;writing-mode: horizontal-tb;width: 100%;box-sizing: border-box;font-family: var(--mainSans), sans-serif;font-weight: 200;font-size: 15px;line-height: 160%; color: black; font-size: 15px;text-align: center;border-radius: 4px; margin-bottom: 10px;';
  }

  private getLoginInputs(): LoginInputs {
    const username = this._usernameInput;
    const password = this._passwordInput;
    return {
      username: username instanceof HTMLInputElement ? username : undefined,
      password: password instanceof HTMLInputElement ? password : undefined,
    };
  }

  private monitoringBaseMetadata() {
    return {
      mode: this.mode || 'signin',
      linkMode: this.isLinkMode,
      native: runningUnderNativePlatform(),
      route: window.location.pathname,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    };
  }

  private inputSnapshot(input: HTMLInputElement | undefined) {
    if (!input) {
      return { present: false };
    }

    const style = window.getComputedStyle(input);
    const rect = input.getBoundingClientRect();
    return {
      present: true,
      type: input.type,
      name: input.name,
      autocomplete: input.autocomplete,
      disabled: input.disabled,
      readOnly: input.readOnly,
      tabIndex: input.tabIndex,
      ariaDisabled: input.getAttribute('aria-disabled'),
      display: style.display,
      visibility: style.visibility,
      opacity: style.opacity,
      pointerEvents: style.pointerEvents,
      width: Math.round(rect.width),
      height: Math.round(rect.height),
    };
  }

  private inputAnomalies(field: LoginInputField, input: HTMLInputElement | undefined): string[] {
    if (!input) {
      return [`${field}:missing`];
    }

    const style = window.getComputedStyle(input);
    const rect = input.getBoundingClientRect();
    const anomalies: string[] = [];
    if (input.disabled) anomalies.push(`${field}:disabled`);
    if (input.readOnly) anomalies.push(`${field}:readonly`);
    if (style.display === 'none') anomalies.push(`${field}:display-none`);
    if (style.visibility === 'hidden') anomalies.push(`${field}:visibility-hidden`);
    if (style.pointerEvents === 'none') anomalies.push(`${field}:pointer-events-none`);
    if (rect.width < 8 || rect.height < 8) anomalies.push(`${field}:zero-sized`);
    return anomalies;
  }

  private checkLoginInputMount() {
    if (this.reportedMountAnomaly) {
      return;
    }

    const inputs = this.getLoginInputs();
    const anomalies = [
      ...this.inputAnomalies('username', inputs.username),
      ...this.inputAnomalies('password', inputs.password),
    ];
    if (!anomalies.length) {
      return;
    }

    this.reportedMountAnomaly = true;
    recordEvent('dabih_login_form_anomaly', {
      ...this.monitoringBaseMetadata(),
      stage: 'mount',
      anomalies,
      usernameInput: this.inputSnapshot(inputs.username),
      passwordInput: this.inputSnapshot(inputs.password),
    });
  }

  private fieldAtPoint(event: PointerEvent, inputs: LoginInputs): LoginInputField | undefined {
    for (const field of ['username', 'password'] as const) {
      const input = inputs[field];
      if (!input) continue;

      const rect = input.getBoundingClientRect();
      if (
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom
      ) {
        return field;
      }
    }

    return undefined;
  }

  private readonly onDocumentPointerDown = (event: PointerEvent) => {
    if (this.reportedPointerBlocked || !this.isConnected) {
      return;
    }

    const inputs = this.getLoginInputs();
    const field = this.fieldAtPoint(event, inputs);
    const input = field ? inputs[field] : undefined;
    if (!field || !input || event.composedPath().includes(input)) {
      return;
    }

    const target = event.target instanceof Element ? event.target : undefined;
    this.reportedPointerBlocked = true;
    recordEvent('dabih_login_form_anomaly', {
      ...this.monitoringBaseMetadata(),
      stage: 'pointerdown',
      anomalies: [`${field}:pointer-blocked`],
      field,
      pointerTargetTag: target?.tagName,
      pointerTargetSlot: target?.getAttribute('slot'),
      usernameInput: this.inputSnapshot(inputs.username),
      passwordInput: this.inputSnapshot(inputs.password),
    });
  };

  private onLoginInputKeydown(event: KeyboardEvent, field: LoginInputField) {
    if (this.reportedKeyWithoutInput || event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) {
      return;
    }

    const input = event.currentTarget;
    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    const valueBefore = input.value;
    window.setTimeout(() => {
      if (this.reportedKeyWithoutInput || !this.isConnected || input.value !== valueBefore) {
        return;
      }

      const inputs = this.getLoginInputs();
      this.reportedKeyWithoutInput = true;
      recordEvent('dabih_login_form_anomaly', {
        ...this.monitoringBaseMetadata(),
        stage: 'keydown',
        anomalies: [`${field}:key-without-input`],
        field,
        usernameInput: this.inputSnapshot(inputs.username),
        passwordInput: this.inputSnapshot(inputs.password),
      });
    }, 500);
  }

  private setupLoginFormInstrumentation() {
    this.instrumentationAbortController?.abort();
    this.instrumentationAbortController = new AbortController();
    const { signal } = this.instrumentationAbortController;
    const inputs = this.getLoginInputs();

    inputs.username?.addEventListener('keydown', (event) => this.onLoginInputKeydown(event, 'username'), { signal });
    inputs.password?.addEventListener('keydown', (event) => this.onLoginInputKeydown(event, 'password'), { signal });
    document.addEventListener('pointerdown', this.onDocumentPointerDown, { capture: true, signal });

    requestAnimationFrame(() => {
      this.checkLoginInputMount();
    });
  }

  render() {
    return html`
      <form id="loginForm" @submit=${this.login}>
        <div class="logo"></div>

        ${this.isLinkMode ? html` <div>${t('login.linkIntro', { provider: this.providerDescription })}</div>` : nothing}

        <!-- <input
                  id="loginName"
                  placeholder="epost"
                  type="email"
                  name="loginName"
                  .value=this.username
                  @input=(event) => (this.username = event.target.value)}
                  autocomplete="username"
                /> -->
        <slot name="usernameInput" id="loginName"></slot>
        ${this.loginErrorInvalidEmail ? html` <p class="error">${t('login.invalidEmail')}</p> ` : nothing}

        <slot id="password" name="passwordInput"></slot>
        <!-- <input
                  id="password"
                  placeholder="passord"
                  name="password"
                  type="password"
                  .value=this.password}
                  autocomplete="current-password"
                  @input=(event) => (this.password = event.target.value)}
                /> -->
        ${this.loginErrorMissingPassword ? html` <div>${t('login.missingPassword')}</div> ` : nothing}
        ${this.loginFailed ? html` <div>${t('login.failed')}</div> ` : nothing}

        <input
          type="submit"
          class="action"
          value="${this.isLinkMode ? t('login.linkSubmit', { provider: this.providerDescription }) : t('login.submit')}"
        />

        <div class="alternatives">
          <div @click=${this.sendPassword}>${t('login.sendNewPassword')}</div>
          ${runningUnderNativePlatform()
            ? nothing
            : html` <div @click=${this.signup}>${t('login.signupCta')}</div>
                <div @click=${this.gotoSite}>${t('login.learnMore')}</div>`}
        </div>

        ${!this.isLinkMode
          ? html` <div class="secureLogin">
              <h2>${t('login.secureHeading')}</h2>
              <p>${t('login.secureBody')}</p>
              <input class="action" @click=${this.loginAuthCriipto} type="button" value="BankID" />
              <input class="action" @click=${this.loginAuth} type="button" value="HelseID" />
            </div>`
          : nothing}
      </form>
    `;
  }

  protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    const usernameInput = this._usernameInput as HTMLInputElement;
    const passwordInput = this._passwordInput as HTMLInputElement;
    if (usernameInput && passwordInput) {
      const inputStyle = this.getInputStyle();
      usernameInput.style.cssText = inputStyle;
      passwordInput.style.cssText = inputStyle;
      usernameInput.addEventListener('input', (e) => this.onLoginNameChange(e));
      passwordInput.addEventListener('input', (e) => this.onPasswordChange(e));
    }

    this.setupLoginFormInstrumentation();

    // Check for auto-login parameters
    this.checkAutoLogin();
  }

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('username') && this._usernameInput) {
      (this._usernameInput as HTMLInputElement).value = this.username;
    }

    if (_changedProperties.has('password') && this._passwordInput) {
      (this._passwordInput as HTMLInputElement).value = this.password;
    }

    // Check for auto-login when locationSearch changes
    if (_changedProperties.has('locationSearch') && this.locationSearch) {
      this.checkAutoLogin();
    }
  }

  disconnectedCallback(): void {
    this.instrumentationAbortController?.abort();
    super.disconnectedCallback();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-login': DLogin;
  }
}
